const _excluded = ["trapFocus", "returnFocus", "focusContainer"];

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import * as React from 'react';
import { tabbable } from 'tabbable';
import { versionBridge } from '@sail/version-bridge';
const {
  useCallback,
  useEffect,
  useMemo,
  useRef
} = React; // This timer is shared across all instances of FocusTrap on a page.
// When trapFocus and returnFocus are true, FocusTrap returns to the
// previously focused element when FocusTrap is unmounted or when trapFocus
// become false (e.g. ContextualPopover begins its exit transition).
// However, we want to skip this return focus behavior when another
// FocusTrap is being mounted so we don't inadvertently focus/scroll
// to the previous element (in case it is outside the viewport).
// https://jira.corp.stripe.com/browse/SAIL-1436
// @ts-expect-error - TS7034 - Variable 'nextFocusTimer' implicitly has type 'any' in some locations where its type cannot be determined.

let nextFocusTimer = null;

function getFocusableEls(container) {
  return container ? tabbable(container) : [];
} // @ts-expect-error - TS7006 - Parameter 'elem' implicitly has an 'any' type.


function jumpFocus(elem) {
  elem && elem.focus();
}

export function useFocusTrap(trapRef, props) {
  const {
    trapFocus,
    returnFocus,
    focusContainer
  } = props;
  const returnFocusNodeRef = useRef(document.activeElement); // Don't return focus to the previous Menu trigger if we're about to mount
  // another FocusTrap. See above comment on `nextFocusTimer` declaration.
  // @ts-expect-error - TS7005 - Variable 'nextFocusTimer' implicitly has an 'any' type.

  useEffect(() => clearTimeout(nextFocusTimer), []);
  useEffect(() => {
    if (!trapFocus) {
      return;
    }

    const activeElement = document.activeElement;
    const trapEl = trapRef.current;
    const returnFocusNode = returnFocusNodeRef.current; // Jump focus if the trap container exists and if there's currently no focused
    // element or if it's outside the trap container.

    if (trapEl && (!activeElement || !trapEl.contains(activeElement))) {
      const focusableEls = getFocusableEls(trapEl); // focusContainer means we want to always focus on the modal container,
      // even if there are interactive elements in the trap. This is useful when
      // it is undesirable to focus on the first interactive element, e.g. a tooltip

      if (focusContainer) {
        jumpFocus(trapEl);
      } else if (focusableEls.length) {
        jumpFocus(focusableEls[0]);
      }
    }

    return () => {
      if (returnFocus && returnFocusNode && // @ts-expect-error - TS2339 - Property 'focus' does not exist on type 'Element'.
      returnFocusNode.focus && returnFocusNode !== document.body) {
        returnFocusNodeRef.current = null; // @ts-expect-error - TS2339 - Property 'focus' does not exist on type 'Element'.

        nextFocusTimer = setTimeout(() => returnFocusNode.focus());
      }
    };
  }, [trapFocus, returnFocus, focusContainer, trapRef]);
  const onKeyDown = useCallback(e => {
    const trapEl = trapRef.current;

    if (!trapFocus || !trapEl || e.key !== 'Tab') {
      return;
    }

    e.preventDefault();
    const focusableEls = getFocusableEls(trapEl);
    const numFocusableEls = focusableEls.length; // @ts-expect-error - TS2345 - Argument of type 'EventTarget' is not assignable to parameter of type 'FocusableElement'.

    const idx = focusableEls.indexOf(e.target); // If there are no focusable elements in the trap, focus on the container

    if (!numFocusableEls) {
      jumpFocus(trapEl);
      return;
    } // In order to handle custom tabIndex values, we must actually hijack *all*
    // tab/shift+tab events in the trap. Otherwise, if we have one `tabIndex="1"` el in
    // a modal, native tab behavior would be to focus next on the first `tabIndex="0"`
    // (or equivalent) item in the entire document, which would likely move focus
    // outside. This lets us maintain the correct tab index ordering, but "scoped"
    // to within the trap.


    if (e.shiftKey) {
      // We want to tab to the "previous" item; if "previous" is out of bounds,
      // go to the last item
      jumpFocus(focusableEls[idx - 1 < 0 ? numFocusableEls - 1 : idx - 1]);
    } else {
      // We want to tab to the "next" item; if "next" is out of bounds,
      // go to the first item
      jumpFocus(focusableEls[idx + 1 >= numFocusableEls ? 0 : idx + 1]);
    }
  }, [trapFocus, trapRef]);
  return useMemo(() => ({
    onKeyDown,
    tabIndex: -1
  }), [onKeyDown]);
}

const FocusTrap = props => {
  const {
    trapFocus,
    returnFocus,
    focusContainer
  } = props,
        rest = _objectWithoutProperties(props, _excluded);

  const trapRef = useRef(null);
  const focusTrapProps = useFocusTrap(trapRef, {
    trapFocus,
    returnFocus,
    focusContainer
  }); // check if a sail-next focus scope has been registered, if so, use it for compat.

  const {
    FocusScope
  } = versionBridge;
  const content = /*#__PURE__*/React.createElement("span", _extends({
    ref: trapRef
  }, focusTrapProps, rest));
  return FocusScope ? /*#__PURE__*/React.createElement(FocusScope, null, content) : content;
};

export default FocusTrap;